const JOBS_CACHE_KEY = "sift:jobs-cache";
const CACHE_VERSION = 1;

function formatDurationLabel(durationSec) {
  if (!Number.isFinite(durationSec) || durationSec <= 0) {
    return null;
  }
  if (durationSec < 90) {
    return `${Math.max(1, Math.round(durationSec))}s`;
  }
  const minutes = Math.round(durationSec / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (remMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remMinutes}m`;
}

const EFFECTIVE_RTF = 0.1;
const PROGRESS_CAP = 95;

function computeFakeProgress(elapsedSec, durationSec) {
  if (elapsedSec <= 0 || durationSec <= 0) return 1;
  const estimatedSec = durationSec * EFFECTIVE_RTF;
  const ratio = 1 - Math.exp(-elapsedSec / estimatedSec);
  return Math.min(PROGRESS_CAP, Math.max(1, Math.round(ratio * PROGRESS_CAP)));
}

function mapJobStatus(status) {
  switch ((status || "").toLowerCase()) {
    case "completed":
      return "complete";
    case "running":
      return "transcribing";
    case "failed":
      return "failed";
    case "queued":
    default:
      return "queued";
  }
}

function computeRunningProgress(job, fakeProgressTracker) {
  if (job.segments_total == null) return null; // warmup → indeterminate
  if (job.segments_total > 1) {
    // multi-segment: real progress
    return Math.round((job.segments_completed / job.segments_total) * 100);
  }
  // single-segment (segments_total <= 1)
  if (job.segments_completed >= job.segments_total) return 100;
  const durationSec = Number(job.duration_sec);
  if (!Number.isFinite(durationSec) || durationSec <= 0) return null;
  if (!fakeProgressTracker.has(job.id)) {
    fakeProgressTracker.set(job.id, Date.now());
  }
  const elapsedSec = (Date.now() - fakeProgressTracker.get(job.id)) / 1000;
  return computeFakeProgress(elapsedSec, durationSec);
}

function createFileFromJob(job, fakeProgressTracker) {
  const requestedAt = job.requested_at
    ? new Date(job.requested_at)
    : new Date();
  const durationSec = Number(job.duration_sec);
  let progress;
  if (job.status === "completed") {
    progress = 100;
  } else if (job.status === "running") {
    progress = computeRunningProgress(job, fakeProgressTracker);
  } else {
    progress = null;
  }
  const file = {
    id: job.id,
    jobId: job.id,
    name: job.id,
    displayName: job.title || job.id,
    status: mapJobStatus(job.status),
    progress,
    created: requestedAt,
    duration: formatDurationLabel(durationSec),
    durationMinutes: Number.isFinite(durationSec)
      ? Math.round(durationSec / 60)
      : null,
    mediaId: job.media_id || null,
    mediaR2Key: job.media_r2_key || null,
    preview_snippet: job.preview_snippet || null,
    transcriptR2Key: job.transcript_r2_key || null,
    requestedAtIso: job.requested_at || requestedAt.toISOString(),
    failureReason: job.failure_reason || null,
  };
  return file;
}

export function createJobsStore() {
  const files = [];
  const fakeProgressTracker = new Map(); // jobId -> startTimeMs

  // load cached jobs from localStorage - returns true if cache was valid
  function loadFromCache() {
    try {
      const raw = localStorage.getItem(JOBS_CACHE_KEY);
      if (!raw) return false;
      const cached = JSON.parse(raw);
      if (cached.version !== CACHE_VERSION || !Array.isArray(cached.jobs)) {
        return false;
      }
      files.length = 0;
      cached.jobs.forEach((file) => {
        // restore date objects from ISO strings
        file.created = new Date(file.requestedAtIso);
        files.push(file);
      });
      return files.length > 0;
    } catch {
      return false;
    }
  }

  // save current jobs to localStorage
  function saveToCache() {
    try {
      const payload = {
        version: CACHE_VERSION,
        jobs: files.filter((f) => f.jobId), // only cache server-confirmed jobs
        cachedAt: Date.now(),
      };
      localStorage.setItem(JOBS_CACHE_KEY, JSON.stringify(payload));
    } catch {
      // localStorage full or unavailable - ignore
    }
  }

  function replaceWithJobs(jobs = []) {
    files.length = 0;
    // clean up tracker for jobs no longer running
    const runningIds = new Set(
      jobs.filter((j) => j.status === "running").map((j) => j.id),
    );
    for (const id of fakeProgressTracker.keys()) {
      if (!runningIds.has(id)) fakeProgressTracker.delete(id);
    }
    const mapped = jobs.map((job) =>
      createFileFromJob(job, fakeProgressTracker),
    );
    mapped
      .sort((a, b) => {
        return (
          new Date(b.requestedAtIso).getTime() -
          new Date(a.requestedAtIso).getTime()
        );
      })
      .forEach((file) => files.push(file));
    // persist to cache after fresh data arrives
    saveToCache();
  }

  function upsertJob(job) {
    const existing = files.find((file) => file.jobId === job.id);
    if (!existing) {
      const next = createFileFromJob(job, fakeProgressTracker);
      files.unshift(next);
      return next;
    }
    existing.status = mapJobStatus(job.status);
    if (existing.status === "complete") {
      existing.progress = 100;
      fakeProgressTracker.delete(job.id);
    } else if (job.status === "running") {
      existing.progress = computeRunningProgress(job, fakeProgressTracker);
    }
    existing.displayName = job.title || existing.displayName;
    existing.preview_snippet = job.preview_snippet || existing.preview_snippet;
    existing.transcriptR2Key =
      job.transcript_r2_key || existing.transcriptR2Key;
    if (job.duration_sec != null) {
      const durationSec = Number(job.duration_sec);
      if (Number.isFinite(durationSec)) {
        existing.duration = formatDurationLabel(durationSec);
        existing.durationMinutes = Math.round(durationSec / 60);
      }
    }
    if (job.requested_at) {
      existing.requestedAtIso = job.requested_at;
      existing.created = new Date(job.requested_at);
    }
    if (job.failure_reason) {
      existing.failureReason = job.failure_reason;
    }
    return existing;
  }

  function createPendingFile({ filename, sizeBytes }) {
    const tempId = `pending-${Math.random().toString(36).slice(2)}`;
    const entry = {
      id: tempId,
      jobId: null,
      name: tempId,
      displayName: filename || "pending upload",
      status: "uploading",
      progress: 0,
      created: new Date(),
      duration: null,
      durationMinutes: null,
      sizeBytes: sizeBytes ?? null,
      localFile: null,
      requestedAtIso: new Date().toISOString(),
      preview_snippet: null,
      transcriptR2Key: null,
      failureReason: null,
    };
    files.unshift(entry);
    return entry;
  }

  function attachLocalFile(entry, file) {
    if (!entry) return;
    entry.localFile = file || null;
  }

  function linkPendingToJob(entry, job) {
    if (!entry || !job) return;
    entry.jobId = job.id;
    entry.name = job.id;
    entry.displayName = job.title || entry.displayName;
    entry.status = mapJobStatus(job.status);
    entry.progress = entry.status === "complete" ? 100 : entry.progress;
    entry.requestedAtIso = job.requested_at || entry.requestedAtIso;
    entry.created = job.requested_at
      ? new Date(job.requested_at)
      : entry.created;
    entry.preview_snippet = job.preview_snippet || entry.preview_snippet;
    entry.transcriptR2Key = job.transcript_r2_key || entry.transcriptR2Key;
    entry.mediaId = job.media_id || entry.mediaId;
    entry.mediaR2Key = job.media_r2_key || entry.mediaR2Key;
    if (job.duration_sec != null) {
      const durationSec = Number(job.duration_sec);
      if (Number.isFinite(durationSec)) {
        entry.duration = formatDurationLabel(durationSec);
        entry.durationMinutes = Math.round(durationSec / 60);
      }
    }
  }

  function removeFileById(id) {
    const index = files.findIndex((file) => file.id === id);
    if (index >= 0) {
      files.splice(index, 1);
      saveToCache();
    }
  }

  function findByName(name) {
    return files.find((file) => file.name === name) || null;
  }

  function findByJobId(jobId) {
    return files.find((file) => file.jobId === jobId) || null;
  }

  function getApproximateUsageMinutes() {
    return files
      .filter((file) => file.status === "complete" && file.durationMinutes)
      .reduce((sum, file) => sum + (file.durationMinutes || 0), 0);
  }

  return {
    files,
    loadFromCache,
    saveToCache,
    replaceWithJobs,
    upsertJob,
    createPendingFile,
    attachLocalFile,
    linkPendingToJob,
    removeFileById,
    findByName,
    findByJobId,
    getApproximateUsageMinutes,
  };
}
